import { useState } from 'react';
import { signIn } from './supabaseClient';
import { COMPTE_STAGING, adressePourConnexion } from './services/comptesConnexion';

// Le compte réellement visé par le formulaire. Sur le staging, ce n'est pas
// celui d'Anissa : sa base ne contient qu'un compte synthétique.
const compteVise = adressePourConnexion('Anissa', import.meta.env);

// ─── LoginScreen — la porte d'entrée de cockpit.anissanutrition.ch ───────
// 2026-07-21 (espace 100 % Anissa, phase branding) : page de connexion
// dédiée, dans sa charte. Vert profond, halos qui respirent, entrée en
// cascade (monogramme → titre → filet → formulaire) — même grammaire
// d'animation que le splash de l'app cliente. prefers-reduced-motion
// respecté (tout est en CSS, coupé par la media query).
const ANISSA_AVATAR = 'https://cdn.prod.website-files.com/699eb56ec2e8b94e41cfa06c/69d411dfafbbe967e3d992c4_Design_sans_titre_1_-removebg-preview.png';

export default function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await signIn('Anissa', password);
      // 2026-07-26 — Suivi mobile : localStorage pour que la connexion
      // survive aux relances de la PWA sur téléphone (avant : sessionStorage,
      // mot de passe redemandé à chaque ouverture).
      localStorage.setItem('bfc_auth', 'true');
      localStorage.setItem('bfc_user', 'Anissa');
      onLogin('Anissa');
    } catch (err) {
      setError('Mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="anissa-login">
      <style>{`
        .anissa-login {
          position: fixed; inset: 0; overflow: hidden;
          display: flex; align-items: center; justify-content: center;
          background: radial-gradient(1100px 700px at 50% 18%, #24402d 0%, #16281c 55%, #0f1a13 100%);
          padding: 24px;
        }
        /* Halos organiques qui respirent — l'ambiance, pas un feu d'artifice */
        .anissa-login__glow {
          position: absolute; border-radius: 50%; filter: blur(90px);
          pointer-events: none; will-change: transform, opacity;
        }
        .anissa-login__glow--sage {
          width: 480px; height: 480px; left: -120px; top: -100px;
          background: rgba(93,138,110,0.22);
          animation: anl-drift1 16s ease-in-out infinite;
        }
        .anissa-login__glow--gold {
          width: 380px; height: 380px; right: -100px; bottom: -80px;
          background: rgba(184,134,42,0.14);
          animation: anl-drift2 19s ease-in-out infinite;
        }
        @keyframes anl-drift1 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: .8; }
          50% { transform: translate(50px, 30px) scale(1.12); opacity: 1; }
        }
        @keyframes anl-drift2 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: .7; }
          50% { transform: translate(-40px, -30px) scale(1.15); opacity: 1; }
        }
        .anissa-login__card {
          position: relative; width: 100%; max-width: 400px;
          background: rgba(250,249,246,0.045);
          border: 1px solid rgba(250,249,246,0.10);
          border-radius: 26px; padding: 44px 38px 30px;
          backdrop-filter: blur(14px);
          box-shadow: 0 24px 80px -24px rgba(0,0,0,0.55);
          text-align: center;
        }
        .anissa-login__logo {
          width: 88px; height: 88px; object-fit: contain; margin: 0 auto 6px;
          display: block;
          filter: drop-shadow(0 0 22px rgba(93,138,110,0.45)) brightness(1.9);
          animation: anl-rise .8s cubic-bezier(.32,.72,0,1) .15s backwards,
                     anl-breathe 6s ease-in-out 2s infinite;
        }
        @keyframes anl-breathe {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .anissa-login__eyebrow {
          font-size: 10px; letter-spacing: .24em; text-transform: uppercase;
          color: #c9a96a; font-weight: 600; margin: 10px 0 6px;
          animation: anl-rise .7s cubic-bezier(.32,.72,0,1) .4s backwards;
        }
        .anissa-login__title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 30px; color: #f5f2e9; margin: 0; line-height: 1.15;
          animation: anl-rise .7s cubic-bezier(.32,.72,0,1) .55s backwards;
        }
        .anissa-login__rule {
          width: 36px; height: 1px; margin: 18px auto;
          background: rgba(201,169,106,0.65); transform-origin: center;
          animation: anl-rule .6s cubic-bezier(.32,.72,0,1) .8s backwards;
        }
        @keyframes anl-rule { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        .anissa-login__hint {
          font-size: 13.5px; color: rgba(245,242,233,0.55); margin: 0 0 22px;
          animation: anl-rise .7s cubic-bezier(.32,.72,0,1) .9s backwards;
        }
        .anissa-login__form { animation: anl-rise .7s cubic-bezier(.32,.72,0,1) 1.05s backwards; }
        .anissa-login__form label {
          display: block; text-align: left; font-size: 10.5px; font-weight: 600;
          letter-spacing: .16em; text-transform: uppercase;
          color: rgba(245,242,233,0.5); margin: 0 0 8px 2px;
        }
        .anissa-login__form input {
          width: 100%; box-sizing: border-box;
          background: rgba(15,26,19,0.55);
          border: 1px solid rgba(250,249,246,0.14);
          border-radius: 14px; padding: 14px 16px;
          font-size: 16px; color: #f5f2e9; letter-spacing: .12em;
          outline: none; transition: border-color .2s ease, box-shadow .2s ease;
        }
        .anissa-login__form input:focus {
          border-color: rgba(93,138,110,0.8);
          box-shadow: 0 0 0 4px rgba(93,138,110,0.18);
        }
        .anissa-login__error {
          margin: 12px 0 0; font-size: 13px; font-weight: 600; color: #e89090;
          animation: anl-shake .35s ease;
        }
        @keyframes anl-shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .anissa-login__btn {
          width: 100%; margin-top: 18px; border: none; cursor: pointer;
          border-radius: 14px; padding: 15px 16px;
          font-size: 15px; font-weight: 600; color: #f5f2e9;
          background: linear-gradient(135deg, #4a7a5c 0%, #2E5B40 100%);
          box-shadow: 0 10px 28px -10px rgba(46,91,64,0.65);
          transition: transform .15s ease, box-shadow .2s ease, filter .2s ease;
        }
        .anissa-login__btn:hover { filter: brightness(1.08); box-shadow: 0 14px 34px -10px rgba(46,91,64,0.8); }
        .anissa-login__btn:active { transform: scale(.985); }
        .anissa-login__btn:disabled { opacity: .6; cursor: wait; }
        .anissa-login__compte {
          margin-top: 10px; font-size: 11px; letter-spacing: .04em;
          color: rgba(245,242,233,0.5); text-align: center;
        }
        .anissa-login__footer {
          margin-top: 26px; font-size: 11px; letter-spacing: .06em;
          color: rgba(245,242,233,0.35);
          animation: anl-rise .7s cubic-bezier(.32,.72,0,1) 1.25s backwards;
        }
        @keyframes anl-rise {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .anissa-login * { animation: none !important; }
        }
      `}</style>

      <div className="anissa-login__glow anissa-login__glow--sage" aria-hidden />
      <div className="anissa-login__glow anissa-login__glow--gold" aria-hidden />

      <div className="anissa-login__card">
        <img src={ANISSA_AVATAR} alt="" aria-hidden className="anissa-login__logo" />
        <p className="anissa-login__eyebrow">Anissa Deroubaix · Nutrition</p>
        <h1 className="anissa-login__title">Espace Nutrition</h1>
        <div className="anissa-login__rule" aria-hidden />
        <p className="anissa-login__hint">
          {compteVise === COMPTE_STAGING
            ? 'Environnement de test. Compte synthétique, aucune donnée réelle.'
            : 'Bienvenue Anissa. Ton espace t’attend.'}
        </p>

        <form onSubmit={handleLogin} className="anissa-login__form">
          <label htmlFor="login-pw">Mot de passe</label>
          <input
            id="login-pw"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
            autoFocus
          />
          {error && <div className="anissa-login__error">{error}</div>}
          {/* 2026-08-02 — sur le staging, l'écran DIT quel compte il vise.
              Sans ça, une erreur d'identifiants ne pouvait que s'interpréter
              comme un mot de passe faux, alors que l'adresse visée n'existait
              pas dans cette base. L'adresse d'un compte fictif n'est pas un
              secret ; en production, rien de tout ceci ne s'affiche. */}
          {compteVise === COMPTE_STAGING && (
            <div className="anissa-login__compte">Compte : {COMPTE_STAGING}</div>
          )}
          <button type="submit" className="anissa-login__btn" disabled={loading}>
            {loading ? 'Ouverture…' : 'Entrer dans mon espace'}
          </button>
        </form>

        <div className="anissa-login__footer">
          Espace Nutrition — Anissa Deroubaix
        </div>
      </div>
    </div>
  );
}
